import React, { useState } from 'react';
import { Box, Button, CircularProgress, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BannerStyles } from '../styles/banner';
import { useAppSelector } from '../../store/hook';
import clsx from 'clsx';
import ReactHtmlParser from 'html-react-parser';
import { formRoutes, routeMappings, SessionStore } from '../../constants';
import { useNavigate } from 'react-router-dom';
import { appendCdnToImagePath } from '../../common/utils/appendCdnToImagePath';
import { redirectUrlOnAuthentication } from '../../common/utils/redirectUrlOnAuthentication';
import { Template } from '../../store/interfaces';
import { getStore } from '../../store';
import { useTranslation } from 'react-i18next';
import { isTimeExpired } from '../../taskList/utils/stageTimeUtils';
import { getFormatedBannerDates, areDatesEqual } from '../../common/utils/dateFormatter';
import { getExperienceInitCookie } from '../../common/utils/experienceInitCookie';

interface BannerProps {
  getNextWorkflowItem?(template?: Template): Promise<void>;
}

const Banner = (props: BannerProps): React.JSX.Element => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const template = useAppSelector((state) => state.template);
  const { settings } = template;
  const candidate = useAppSelector((state) => state.candidate);
  const {
    event,
    event: { bannerImage, bannerColor, bannerImageEnabled, textColor },
  } = settings;
  const { classes } = BannerStyles({
    bannerImage: bannerImage && appendCdnToImagePath(bannerImage.path),
    bannerColor,
    textColor,
  });

  const { userSessionActive } = useAppSelector((state) => state.basicDetails);
  const { registrationEndDate, projectStartDate, projectEndDate } = useAppSelector((state) => state.projectSettings);

  const bannerDateMessage = getFormatedBannerDates(projectStartDate, true, projectEndDate);
  const eventStartOnDate = getFormatedBannerDates(projectStartDate, false);
  const eventEndsOnDate = getFormatedBannerDates(projectEndDate, false);
  const registrationDate = getFormatedBannerDates(registrationEndDate, false);
  const projectStarted = isTimeExpired(projectStartDate);
  const projectEnded = isTimeExpired(projectEndDate);
  const showRegistrationDate = !areDatesEqual(projectEndDate, registrationEndDate);

  const expCookie = getExperienceInitCookie();
  const candidateExists = (expCookie && expCookie.candidateExists) || false;
  const showRegister: boolean =
    !candidateExists &&
    ((!registrationEndDate && projectStarted && !projectEnded) ||
      (registrationEndDate && !isTimeExpired(registrationEndDate)));

  const handleTaskClick = async (): Promise<void> => {
    if (props.getNextWorkflowItem) {
      setLoading(true);
      await props.getNextWorkflowItem(template);
      const currentWorkflowItem = getStore()?.state?.currentWorkflowItem;
      const url = await redirectUrlOnAuthentication(currentWorkflowItem, { template, candidate });
      if (formRoutes.includes(url)) {
        sessionStorage.setItem(SessionStore.isFormCompleted, 'false');
      }
      navigate(url);
      setLoading(false);
    }
  };

  return (
    <Grid
      container
      className={clsx({
        [classes.backgroundImage]: bannerImageEnabled,
        [classes.bgColorContainer]: !bannerImageEnabled,
        [classes.container]: true,
        'banner-section': true,
      })}>
      <Grid item xs={lgUp ? 6 : 12} className={classes.contentSection}>
        <Box>
          <Box>
            <Typography color="inherit" className={classes.eventHeading}>
              {event.name}
            </Typography>
            <Typography color="inherit" className={classes.subHeading}>
              {ReactHtmlParser(event.description)}
            </Typography>
          </Box>
          <Box>
            <Box>
              <Typography color="inherit" className={classes.projectDateSection}>
                {bannerDateMessage}
              </Typography>
            </Box>
            <Typography color="inherit" className={classes.eventStartSection}>
              {!projectStarted ? (
                <>{t('eventStartsOn', { date: eventStartOnDate })}</>
              ) : (
                <>
                  {!projectEnded ? (
                    <>{t('eventEndsOn', { date: eventEndsOnDate })}</>
                  ) : (
                    <>{t('eventEnded', { date: eventEndsOnDate })}</>
                  )}
                </>
              )}
            </Typography>
            {registrationEndDate && showRegistrationDate && (
              <Typography color="inherit" className={classes.registerationCloseSection}>
                {!isTimeExpired(registrationEndDate) ? (
                  <>
                    {t('registerBy', {
                      date: `${registrationDate}`,
                    })}
                  </>
                ) : (
                  <>
                    {t('registrationEnded', {
                      date: `${registrationDate}`,
                    })}
                  </>
                )}
              </Typography>
            )}

            <Box pt={2.5}>
              {userSessionActive && !projectEnded ? (
                <Button
                  data-testid={'task-button'}
                  onClick={handleTaskClick}
                  className={classes.bannerCTA}
                  type="submit"
                  variant="outlined"
                  disabled={loading}
                  color="primary"
                  aria-disabled="false">
                  {t('tasks')}
                  {loading && <CircularProgress size={25} className={classes.buttonProgress} />}
                </Button>
              ) : showRegister ? (
                <Button
                  className={classes.bannerCTA}
                  onClick={(): void => navigate(routeMappings['LOGIN'])}
                  type="submit"
                  variant="outlined"
                  color="primary"
                  aria-disabled="false">
                  {t('registerNow')}
                </Button>
              ) : (
                <></>
              )}
            </Box>
          </Box>
        </Box>
      </Grid>
      {event.mediaImage && (
        <Grid item xs={lgUp ? 6 : 12} className={classes.mediaContainer}>
          <Box display="flex" justifyContent="center">
            <img height={'100%'} width={'100%'} src={appendCdnToImagePath(event.mediaImage.path)} alt="" />
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default Banner;
